import { Home, Users, Clock, CalendarClock, User, Settings, LogOut, BarChart, FileText, FlaskConical } from "lucide-react";
import { Link } from "react-router-dom";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useAuthStore } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";

interface MenuItem {
  title: string;
  id: string;
  icon: React.ElementType;
  url: string;
  roles: string[];
  external?: boolean;
}

interface SidebarMenuItemsProps {
  activeItem: string;
  setActiveItem: (item: string) => void;
}

import { useState } from "react";
import { TemplatesManagerDialog } from "@/components/prescriptions/TemplatesManagerDialog";

export function SidebarMenuItems({ activeItem, setActiveItem }: SidebarMenuItemsProps) {
  const { user, logout } = useAuthStore();
  const { toast } = useToast();
  const [showTemplates, setShowTemplates] = useState(false);

  // Define menu items based on user role
  const menuItems: MenuItem[] = [
    {
      title: "Dashboard",
      id: "dashboard",
      icon: Home,
      url: "/",
      roles: ['admin', 'staff'],
    },
    {
      title: "Registration",
      id: "registration",
      icon: User,
      url: "/registration",
      roles: ['staff'],
    },
    {
      title: "Queue",
      id: "queue",
      icon: Users,
      url: "/queue",
      roles: ['admin', 'staff'],
    },
    {
      title: "Follow-ups",
      id: "followups",
      icon: CalendarClock,
      url: "/followups",
      roles: ['staff'],
    },
    {
      title: "History",
      id: "history",
      icon: Clock,
      url: "/history",
      roles: ['admin', 'staff'],
    },
    {
      title: "Analytics",
      id: "analytics",
      icon: BarChart,
      url: "/analytics",
      roles: ['admin'],
    },
    {
      title: "Lab Management",
      id: "lab-management",
      icon: FlaskConical,
      url: "https://lab.amcares.in/",
      roles: ['admin', 'staff'],
      external: true,
    },
    {
      title: "Templates",
      id: "templates",
      icon: FileText,
      url: "#",
      roles: ['doctor'],
    },

    {
      title: "Settings",
      id: "settings",
      icon: Settings,
      url: "/settings",
      roles: ['admin'],
    },
  ];
  
  // Filter menu items based on user role
  const filteredMenuItems = user 
    ? menuItems.filter(item => item.roles.includes(user.role))
    : [];
  
  const handleLogout = async () => {
    try {
      // Show logout toast immediately
      toast({
        title: "Logging out...",
        description: "Please wait while we log you out",
      });
      
      await logout();
      
      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your account",
      });
      
      // Force hard navigation to login page to prevent auto-login
      setTimeout(() => {
        // Clear any remaining auth state
        localStorage.clear();
        sessionStorage.clear();
        
        // Force navigation with cache busting
        window.location.replace('/login?logout=' + Date.now());
      }, 500);
      
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        title: "Logout error",
        description: "There was an error logging out. Please try again.",
        variant: "destructive",
      });
      
      // Force navigation even on error
      setTimeout(() => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.replace('/login?error=' + Date.now());
      }, 1000);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <SidebarMenu className="flex-1 space-y-1">
        {filteredMenuItems.map((item) => (
          <SidebarMenuItem
            key={item.id}
            data-active={activeItem === item.id}
            onClick={(e: any) => {
              if (item.id === 'templates') {
                e.preventDefault();
                setActiveItem(item.id);
                setShowTemplates(true);
                return;
              }
              if (item.external) {
                // Open external systems in a new tab; keep the HMS as the active item
                return;
              }
              setActiveItem(item.id);
            }}
            className="px-3 py-2 rounded-md transition-colors hover:bg-accent hover:text-accent-foreground data-[active=true]:bg-hospital-primary data-[active=true]:text-white"
          >
            {item.external ? (
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 w-full"
              >
                <item.icon className="h-5 w-5" />
                <span className="font-medium">{item.title}</span>
              </a>
            ) : (
              <Link to={item.url} className="flex items-center gap-3 w-full">
                <item.icon className="h-5 w-5" />
                <span className="font-medium">{item.title}</span>
              </Link>
            )}
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
      
      <div className="mt-auto pt-4 border-t">
        <SidebarMenuButton 
          onClick={handleLogout}
          className="px-3 py-2 rounded-md transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <LogOut className="h-5 w-5" />
          <span className="font-medium">Logout</span>
        </SidebarMenuButton>
      </div>

      <TemplatesManagerDialog isOpen={showTemplates} onClose={() => setShowTemplates(false)} />
    </div>
  );
}
